import { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {
  exportIcs, getAttendees, getCalendarEvents, getGoogleCalendarEvents,
  importIcs, skipRecurringOccurrence, updatePlannerDate,
} from "./CalendarService";
import { createItem, deleteItem, getItemById, updateItem, updateItemStatus } from "./planner/PlannerService";
import "./CalendarPage.css";

const TYPE_OPTIONS = ["TODO", "IMPORTANT_DATE", "PAYMENT", "REMINDER"];
const FILTER_TYPE_OPTIONS = [...TYPE_OPTIONS, "GOOGLE"];
const RECURRENCE_OPTIONS = ["NONE", "DAILY", "WEEKDAYS", "WEEKLY", "MONTHLY", "HALF_YEARLY", "YEARLY"];
const CATEGORY_OPTIONS = [
  "WORK", "PERSONAL", "FINANCE", "HEALTH", "FAMILY", "EDUCATION",
  "BUSINESS", "SHOPPING", "TRAVEL", "BILLING", "INVESTMENT",
  "SUBSCRIPTION", "INSURANCE", "HOME", "SOCIAL", "GOALS", "OTHER", "PROJECT",
];

const TYPE_LABELS = {
  TODO: "Task", IMPORTANT_DATE: "Date", PAYMENT: "Payment", REMINDER: "Reminder", GOOGLE: "Google",
};
const TYPE_ICONS = {
  TODO: "✅", IMPORTANT_DATE: "📌", PAYMENT: "💳", REMINDER: "🔔", GOOGLE: "📆",
};
const VIEW_LABELS = { timeGridWeek: "Week", dayGridMonth: "Month", listWeek: "Agenda" };

export default function CalendarPage() {
  const calendarRef = useRef(null);
  const importInputRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [activeView, setActiveView] = useState(
    localStorage.getItem("calendar.view") || "timeGridWeek"
  );
  const [filters, setFilters] = useState({
    types: [],
    categories: [],
    includeInactive: false,
    includeRecurring: true,
    query: "",
    includeGoogle: false,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitialDate, setCreateInitialDate] = useState("");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [detailsOccurrenceDate, setDetailsOccurrenceDate] = useState("");
  const [importBusy, setImportBusy] = useState(false);

  const isOwner = () => {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    return auth?.userId === "usr-c25932f5-3093-4ee0-9076-9cd1055fcd55";
  };

  const fetchEvents = async (startStr, endStr) => {
    setError("");
    try {
      const localPromise = getCalendarEvents(startStr, endStr, {
        types: filters.types,
        categories: filters.categories,
        includeInactive: filters.includeInactive,
        includeRecurring: filters.includeRecurring,
      });
      const googlePromise =
        isOwner() && filters.includeGoogle
          ? getGoogleCalendarEvents(startStr, endStr)
          : Promise.resolve([]);

      const [local, google] = await Promise.all([localPromise, googlePromise]);

      const localMapped = (local || []).map((e) => ({
        id: e.id, title: e.title, start: e.start,
        end: e.end || undefined, allDay: e.allDay,
        editable: e.editable, eventStartEditable: e.editable, eventDurationEditable: e.editable,
        extendedProps: { type: e.type, category: e.category, recurrence: e.recurrence, baseId: e.baseId, source: "LOCAL" },
        ...eventColors(e.type, e.category),
      }));

      const googleMapped = (google || []).map((e) => ({
        id: `gcal-${e.id}`, title: e.title || "(No title)",
        start: e.start, end: e.end || undefined, allDay: Boolean(e.allDay),
        editable: false, eventStartEditable: false, eventDurationEditable: false,
        extendedProps: { type: "GOOGLE", category: "OTHER", recurrence: "NONE", baseId: `gcal-${e.id}`, source: "GOOGLE" },
        backgroundColor: "#334155", borderColor: "#334155", textColor: "#ffffff",
      }));

      let merged = [...localMapped, ...googleMapped];
      if (filters.types.length) merged = merged.filter((ev) => filters.types.includes(ev.extendedProps?.type));
      if (filters.categories.length) merged = merged.filter((ev) => filters.categories.includes(ev.extendedProps?.category));
      const q = (filters.query || "").trim().toLowerCase();
      setEvents(q ? merged.filter((ev) => (ev.title || "").toLowerCase().includes(q)) : merged);
    } catch {
      setError("Unable to load calendar data.");
    }
  };

  useEffect(() => {
    if (!range.start || !range.end) return;
    fetchEvents(range.start, range.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, filters.types, filters.categories, filters.includeInactive, filters.includeRecurring, filters.query, filters.includeGoogle]);

  useEffect(() => {
    localStorage.setItem("calendar.view", activeView);
    const api = calendarRef.current?.getApi?.();
    if (api && api.view?.type !== activeView) api.changeView(activeView);
  }, [activeView]);

  const handleCreateEvent = async (payload) => {
    try {
      const created = await createItem(payload);
      const createdDate = calendarDateFromItem(created);
      if (createdDate) {
        setEvents((prev) => [
          ...prev,
          {
            id: created.id, title: created.title,
            start: calendarStartFromItem(created), end: calendarEndFromItem(created),
            allDay: isAllDay(created),
            editable: !created.recurrence || created.recurrence === "NONE",
            extendedProps: { type: created.type, category: created.category, recurrence: created.recurrence, baseId: created.id },
            ...eventColors(created.type, created.category),
          },
        ]);
      }
      setShowCreateModal(false);
    } catch {
      setError("Unable to create event.");
    }
  };

  const handleExportIcs = async () => {
    if (!range.start || !range.end) return;
    setError("");
    try {
      const blob = await exportIcs(range.start, range.end, {
        types: filters.types.filter((t) => t !== "GOOGLE"),
        categories: filters.categories,
        includeInactive: filters.includeInactive,
        includeRecurring: filters.includeRecurring,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "lifeos-calendar.ics";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setError("Unable to export ICS."); }
  };

  const handleImportIcs = async (file) => {
    if (!file) return;
    setError(""); setImportBusy(true);
    try {
      await importIcs(file, { defaultType: "TODO", defaultCategory: "OTHER" });
      if (range.start && range.end) fetchEvents(range.start, range.end);
    } catch { setError("Unable to import ICS."); }
    finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const handleEventClick = async (clickInfo) => {
    const ev = clickInfo.event;
    setDetailsError(""); setDetailsEvent(ev); setDetailsItem(null); setDetailsOpen(true);
    setDetailsOccurrenceDate(ev.id && String(ev.id).includes(":") ? String(ev.id).split(":")[1] : "");
    if (ev.extendedProps?.source !== "LOCAL") return;
    const baseId = ev.extendedProps?.baseId || ev.id;
    if (!baseId) return;
    setDetailsLoading(true);
    try {
      const item = await getItemById(baseId);
      setDetailsItem(item);
    } catch { setDetailsError("Unable to load event details."); }
    finally { setDetailsLoading(false); }
  };

  const handleEventDrop = async (info) => {
    if (info.event.extendedProps.recurrence && info.event.extendedProps.recurrence !== "NONE") {
      info.revert(); setError("Recurring events cannot be moved."); return;
    }
    if (info.event.extendedProps?.source && info.event.extendedProps.source !== "LOCAL") {
      info.revert(); setError("This event cannot be moved."); return;
    }
    const id = info.event.id;
    const date = info.event.startStr.slice(0, 10);
    const allDay = info.event.allDay;
    const startTime = allDay ? null : formatTime(info.event.start);
    const endTime = allDay ? null : formatTime(info.event.end);
    try { await updatePlannerDate(id, date, startTime, endTime, allDay); }
    catch { info.revert(); setError("Unable to update date."); }
  };

  const handleEventResize = async (info) => {
    if (info.event.extendedProps.recurrence && info.event.extendedProps.recurrence !== "NONE") {
      info.revert(); setError("Recurring events cannot be resized."); return;
    }
    if (info.event.extendedProps?.source && info.event.extendedProps.source !== "LOCAL") {
      info.revert(); setError("This event cannot be resized."); return;
    }
    const id = info.event.id;
    const date = info.event.startStr.slice(0, 10);
    const allDay = info.event.allDay;
    const startTime = allDay ? null : formatTime(info.event.start);
    const endTime = allDay ? null : formatTime(info.event.end);
    try { await updatePlannerDate(id, date, startTime, endTime, allDay); }
    catch { info.revert(); setError("Unable to update time."); }
  };

  const handleDateClick = (info) => {
    setCreateInitialDate(info.dateStr);
    setShowCreateModal(true);
  };

  const toggleTypeFilter = (type) => {
    setFilters((prev) => ({
      ...prev,
      types: prev.types.includes(type) ? prev.types.filter((t) => t !== type) : [...prev.types, type],
    }));
  };

  const hasActiveFilters = filters.types.length > 0 || !filters.includeRecurring || filters.includeInactive || filters.includeGoogle;

  return (
    <div className="cal-page">
      {/* ── Top bar ── */}
      <div className="cal-topbar">
        <h2 className="cal-title">Calendar</h2>

        <div className="cal-view-switcher">
          {Object.entries(VIEW_LABELS).map(([v, label]) => (
            <button
              key={v}
              className={`cal-view-btn ${activeView === v ? "active" : ""}`}
              onClick={() => setActiveView(v)}
            >
              {label}
            </button>
          ))}
        </div>

        <input
          className="cal-search"
          placeholder="Search events…"
          value={filters.query}
          onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
        />

        <div className="cal-topbar-right">
          <button className="cal-add-btn" onClick={() => { setCreateInitialDate(""); setShowCreateModal(true); }}>
            + Add
          </button>
          <button className="cal-icon-action" onClick={handleExportIcs} title="Export calendar (.ics)">
            ↓ ICS
          </button>
          <button className="cal-icon-action" onClick={() => importInputRef.current?.click()}
                  disabled={importBusy} title="Import .ics file">
            {importBusy ? "…" : "↑ ICS"}
          </button>
          <input ref={importInputRef} type="file" accept=".ics,text/calendar"
                 className="cal-hidden-input" onChange={(e) => handleImportIcs(e.target.files?.[0])} />
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="cal-filterbar">
        <div className="cal-type-pills">
          <button
            className={`cal-type-pill ${filters.types.length === 0 ? "active" : ""}`}
            onClick={() => setFilters((f) => ({ ...f, types: [] }))}
          >
            All
          </button>
          {FILTER_TYPE_OPTIONS.map((t) => (
            <button
              key={t}
              className={`cal-type-pill ${filters.types.includes(t) ? "active" : ""}`}
              onClick={() => toggleTypeFilter(t)}
            >
              {TYPE_ICONS[t]} {TYPE_LABELS[t]}
            </button>
          ))}
        </div>

        <div className="cal-toggle-group">
          <label className={`cal-toggle-pill ${filters.includeRecurring ? "on" : ""}`}>
            <input type="checkbox" checked={filters.includeRecurring}
                   onChange={(e) => setFilters((f) => ({ ...f, includeRecurring: e.target.checked }))} />
            Recurring
          </label>
          <label className={`cal-toggle-pill ${filters.includeInactive ? "on" : ""}`}>
            <input type="checkbox" checked={filters.includeInactive}
                   onChange={(e) => setFilters((f) => ({ ...f, includeInactive: e.target.checked }))} />
            Show done
          </label>
          {isOwner() && (
            <label className={`cal-toggle-pill ${filters.includeGoogle ? "on" : ""}`}>
              <input type="checkbox" checked={filters.includeGoogle}
                     onChange={(e) => setFilters((f) => ({ ...f, includeGoogle: e.target.checked }))} />
              Google
            </label>
          )}
          {hasActiveFilters && (
            <button className="cal-clear-btn" onClick={() =>
              setFilters({ types: [], categories: [], includeInactive: false, includeRecurring: true, query: "", includeGoogle: false })
            }>
              Clear
            </button>
          )}
        </div>
      </div>

      {error && <div className="cal-error">{error}</div>}

      {/* ── Calendar grid ── */}
      <div className="cal-grid-wrap">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={activeView}
          headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
          height="auto"
          eventStartEditable
          eventDurationEditable
          editable
          selectable
          events={events}
          eventClassNames={(arg) => (arg.event.extendedProps?.conflict ? ["cal-event-conflict"] : [])}
          datesSet={(info) => {
            const start = info.startStr.slice(0, 10);
            const end = info.endStr.slice(0, 10);
            setRange({ start, end });
            fetchEvents(start, end);
          }}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
        />
      </div>

      {/* ── Create modal ── */}
      {showCreateModal && (
        <CreateEventModal
          initialDate={createInitialDate}
          isOwner={isOwner()}
          onSave={handleCreateEvent}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* ── Event detail modal ── */}
      {detailsOpen && (
        <div className="cal-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cal-modal">
            <div className="cal-modal-header">
              <span className="cal-modal-title">
                {detailsItem ? `${TYPE_ICONS[detailsItem.type] || "📅"} ${detailsItem.title}` : "Event"}
              </span>
              <button className="cal-modal-close" type="button" onClick={() => setDetailsOpen(false)}>×</button>
            </div>

            {detailsError && <div className="cal-detail-error">{detailsError}</div>}
            {detailsLoading && <div className="cal-detail-loading">Loading…</div>}

            {!detailsLoading && detailsEvent && detailsEvent.extendedProps?.source !== "LOCAL" && (
              <div className="cal-modal-body">
                <div className="cal-detail-row"><span className="cal-detail-k">Title</span><span>{detailsEvent.title}</span></div>
                <div className="cal-detail-row"><span className="cal-detail-k">Start</span><span>{detailsEvent.startStr}</span></div>
                {detailsEvent.endStr && <div className="cal-detail-row"><span className="cal-detail-k">End</span><span>{detailsEvent.endStr}</span></div>}
                <p className="cal-readonly-note">Read-only event from an external source.</p>
              </div>
            )}

            {!detailsLoading && detailsItem && (
              <CalendarDetailsEditor
                item={detailsItem}
                onChange={setDetailsItem}
                occurrenceDate={detailsOccurrenceDate}
                onSave={async () => {
                  setDetailsError("");
                  try {
                    const updated = await updateItem(detailsItem.id, detailsItem);
                    setDetailsItem(updated);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch { setDetailsError("Unable to save changes."); }
                }}
                onDelete={async () => {
                  setDetailsError("");
                  try {
                    await deleteItem(detailsItem.id);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch { setDetailsError("Unable to delete."); }
                }}
                onMarkDone={async () => {
                  setDetailsError("");
                  try {
                    await updateItemStatus(detailsItem.id, "DONE");
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch { setDetailsError("Unable to update status."); }
                }}
                onSkipOccurrence={async (date) => {
                  setDetailsError("");
                  try {
                    await skipRecurringOccurrence(detailsItem.id, date);
                    if (range.start && range.end) fetchEvents(range.start, range.end);
                    setDetailsOpen(false);
                  } catch { setDetailsError("Unable to skip occurrence."); }
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create Event Modal ───────────────────────────────────────────────────────

function CreateEventModal({ initialDate, isOwner, onSave, onClose }) {
  const [form, setForm] = useState({
    title: "", type: "TODO", category: "WORK",
    date: initialDate || "", allDay: true,
    startTime: "", endTime: "",
    recurrence: "NONE", recurrenceEndDate: "",
    attendeeUserIds: [],
  });
  const [showMore, setShowMore] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoaded, setAttendeesLoaded] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadAttendees = () => {
    if (attendeesLoaded) return;
    getAttendees()
      .then((data) => { setAttendees(Array.isArray(data) ? data : []); setAttendeesLoaded(true); })
      .catch(() => setAttendeesLoaded(true));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(buildPlannerPayload(form));
  };

  return (
    <div className="cal-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cal-create-modal">
        <div className="cal-modal-header">
          <span className="cal-modal-title">New Event</span>
          <button className="cal-modal-close" type="button" onClick={onClose}>×</button>
        </div>

        <form className="cal-create-form" onSubmit={handleSubmit}>
          {/* Title */}
          <input
            className="cal-create-title-input"
            autoFocus required
            placeholder="What's happening?"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />

          {/* Type selector */}
          <div className="cal-create-type-row">
            {TYPE_OPTIONS.map((t) => (
              <button
                key={t} type="button"
                className={`cal-type-select-btn ${form.type === t ? "active" : ""}`}
                onClick={() => set("type", t)}
              >
                {TYPE_ICONS[t]} {TYPE_LABELS[t]}
              </button>
            ))}
          </div>

          {/* Date + all-day */}
          <div className="cal-create-row">
            <input
              type="date" className="cal-input" value={form.date}
              onChange={(e) => set("date", e.target.value)}
            />
            <label className="cal-allday-toggle">
              <input
                type="checkbox" checked={form.allDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  set("allDay", checked);
                  if (checked) { set("startTime", ""); set("endTime", ""); }
                }}
              />
              All day
            </label>
          </div>

          {/* Time — only when not all-day */}
          {!form.allDay && (
            <div className="cal-create-row">
              <input
                type="time" className="cal-input" placeholder="Start"
                value={form.startTime} onChange={(e) => set("startTime", e.target.value)}
              />
              <span className="cal-time-sep">–</span>
              <input
                type="time" className="cal-input" placeholder="End"
                value={form.endTime} onChange={(e) => set("endTime", e.target.value)}
              />
            </div>
          )}

          {/* More options */}
          <button type="button" className="cal-more-toggle" onClick={() => { setShowMore((s) => !s); if (!showMore && isOwner) loadAttendees(); }}>
            {showMore ? "▲ Less options" : "▼ More options"}
          </button>

          {showMore && (
            <div className="cal-more-section">
              <div className="cal-create-field">
                <label>Category</label>
                <select className="cal-select" value={form.category} onChange={(e) => set("category", e.target.value)}>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="cal-create-field">
                <label>Recurrence</label>
                <select className="cal-select" value={form.recurrence} onChange={(e) => set("recurrence", e.target.value)}>
                  {RECURRENCE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {form.recurrence !== "NONE" && (
                <div className="cal-create-field">
                  <label>Repeat until</label>
                  <input type="date" className="cal-input" value={form.recurrenceEndDate}
                         onChange={(e) => set("recurrenceEndDate", e.target.value)} />
                </div>
              )}
              {isOwner && attendees.length > 0 && (
                <div className="cal-create-field">
                  <label>Invite</label>
                  <div className="cal-attendees">
                    {attendees.map((person) => (
                      <label key={person.id} className="cal-attendee-item">
                        <input
                          type="checkbox"
                          checked={form.attendeeUserIds.includes(person.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            set("attendeeUserIds", checked
                              ? [...form.attendeeUserIds, person.id]
                              : form.attendeeUserIds.filter((id) => id !== person.id));
                          }}
                        />
                        {person.firstName} {person.lastName}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="cal-create-footer">
            <button type="button" className="cal-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="cal-btn-primary">Create Event</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Event Detail Editor ──────────────────────────────────────────────────────

function CalendarDetailsEditor({ item, onChange, onSave, onDelete, onMarkDone, occurrenceDate, onSkipOccurrence }) {
  const dateValue = item.type === "IMPORTANT_DATE" ? item.eventDate : item.type === "PAYMENT" ? item.paymentDate : item.dueDate;
  const allDay = !item.startTime && !item.endTime;
  const isRecurring = (item.recurrence || "NONE") !== "NONE";

  return (
    <div className="cal-modal-body">
      <div className="cal-edit-grid">
        <div className="cal-edit-field cal-edit-span">
          <label>Title</label>
          <input className="cal-input" value={item.title || ""} onChange={(e) => onChange({ ...item, title: e.target.value })} />
        </div>
        <div className="cal-edit-field cal-edit-span">
          <label>Description</label>
          <textarea className="cal-input cal-textarea" value={item.description || ""}
                    onChange={(e) => onChange({ ...item, description: e.target.value })} />
        </div>
        <div className="cal-edit-field">
          <label>Type</label>
          <select className="cal-select" value={item.type || "TODO"} onChange={(e) => onChange({ ...item, type: e.target.value })}>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <div className="cal-edit-field">
          <label>Status</label>
          <select className="cal-select" value={item.status || "ACTIVE"} onChange={(e) => onChange({ ...item, status: e.target.value })}>
            {["ACTIVE","DONE","SKIPPED","CANCELLED","TRACK"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="cal-edit-field">
          <label>Date</label>
          <input type="date" className="cal-input" value={dateValue || ""}
                 onChange={(e) => {
                   const v = e.target.value;
                   if (item.type === "IMPORTANT_DATE") onChange({ ...item, eventDate: v, dueDate: null, paymentDate: null });
                   else if (item.type === "PAYMENT") onChange({ ...item, paymentDate: v, dueDate: null, eventDate: null });
                   else onChange({ ...item, dueDate: v, eventDate: null, paymentDate: null });
                 }} />
        </div>
        <div className="cal-edit-field">
          <label>Category</label>
          <select className="cal-select" value={item.category || "WORK"} onChange={(e) => onChange({ ...item, category: e.target.value })}>
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="cal-edit-field">
          <label>All day</label>
          <label className="cal-allday-toggle" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={allDay}
                   onChange={(e) => {
                     const checked = e.target.checked;
                     onChange({ ...item, startTime: checked ? null : item.startTime || "09:00:00", endTime: checked ? null : item.endTime || "09:30:00" });
                   }} />
            Enabled
          </label>
        </div>
        <div className="cal-edit-field">
          <label>Recurrence</label>
          <select className="cal-select" value={item.recurrence || "NONE"} onChange={(e) => onChange({ ...item, recurrence: e.target.value })}>
            {RECURRENCE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {!allDay && (
          <>
            <div className="cal-edit-field">
              <label>Start time</label>
              <input type="time" className="cal-input" value={(item.startTime || "").slice(0, 5)}
                     onChange={(e) => onChange({ ...item, startTime: normalizeTime(e.target.value) })} />
            </div>
            <div className="cal-edit-field">
              <label>End time</label>
              <input type="time" className="cal-input" value={(item.endTime || "").slice(0, 5)}
                     onChange={(e) => onChange({ ...item, endTime: normalizeTime(e.target.value) })} />
            </div>
          </>
        )}
        {(item.recurrence || "NONE") !== "NONE" && (
          <div className="cal-edit-field">
            <label>Repeat until</label>
            <input type="date" className="cal-input" value={item.recurrenceEndDate || ""}
                   onChange={(e) => onChange({ ...item, recurrenceEndDate: e.target.value || null })} />
          </div>
        )}
        <div className="cal-edit-field">
          <label>Reminder</label>
          <label className="cal-allday-toggle" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={Boolean(item.reminderEnabled)}
                   onChange={(e) => onChange({ ...item, reminderEnabled: e.target.checked })} />
            Enabled
          </label>
        </div>
        {item.reminderEnabled && (
          <div className="cal-edit-field">
            <label>Days before</label>
            <input type="number" min="0" className="cal-input" value={item.reminderBeforeDays ?? 0}
                   onChange={(e) => onChange({ ...item, reminderBeforeDays: Number(e.target.value) })} />
          </div>
        )}
      </div>

      <div className="cal-modal-actions">
        <button className="cal-btn-primary" type="button" onClick={onSave}>Save</button>
        <button className="cal-btn-secondary" type="button" onClick={onMarkDone}>Mark done</button>
        {isRecurring && occurrenceDate && (
          <button className="cal-btn-secondary" type="button" onClick={() => onSkipOccurrence?.(occurrenceDate)}>
            Skip this occurrence
          </button>
        )}
        <button className="cal-btn-danger" type="button" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

// ─── Pure helpers (unchanged) ─────────────────────────────────────────────────

function buildPlannerPayload(form) {
  const hasTime = Boolean(form.startTime || form.endTime);
  const isAllDayFlag = form.allDay && !hasTime;
  const base = {
    title: form.title, type: form.type, category: form.category,
    priority: "MEDIUM", recurrence: form.recurrence || "NONE",
    recurrenceEndDate: form.recurrence !== "NONE" ? form.recurrenceEndDate || null : null,
    attendeeUserIds: form.attendeeUserIds || [],
  };
  const reminder = form.type === "REMINDER" ? { reminderEnabled: true, reminderBeforeDays: 0 } : {};
  const timeFields = isAllDayFlag ? {} : { startTime: normalizeTime(form.startTime), endTime: normalizeTime(form.endTime) };
  if (!form.date) return { ...base, ...reminder, ...timeFields };
  if (form.type === "IMPORTANT_DATE") return { ...base, ...reminder, ...timeFields, eventDate: form.date };
  if (form.type === "PAYMENT") return { ...base, ...reminder, ...timeFields, paymentDate: form.date };
  return { ...base, ...reminder, ...timeFields, dueDate: form.date };
}

function calendarDateFromItem(item) {
  if (item.type === "IMPORTANT_DATE") return item.eventDate;
  if (item.type === "PAYMENT") return item.paymentDate;
  return item.dueDate;
}

function isAllDay(item) { return !item.startTime && !item.endTime; }

function calendarStartFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date) return null;
  return item.startTime ? `${date}T${item.startTime}` : date;
}

function calendarEndFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date || !item.endTime) return undefined;
  return `${date}T${item.endTime}`;
}

function formatTime(date) {
  if (!date) return null;
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function normalizeTime(value) {
  if (!value) return null;
  return value.length === 5 ? `${value}:00` : value;
}

function eventColors(type, category) {
  const palette = {
    WORK: "#1f4acc", BUSINESS: "#1f4acc", PROJECT: "#1f4acc",
    FINANCE: "#1a6f3b", BILLING: "#1a6f3b", INVESTMENT: "#1a6f3b", INSURANCE: "#1a6f3b", SUBSCRIPTION: "#1a6f3b",
    HEALTH: "#0f766e", FAMILY: "#b45309", PERSONAL: "#2563eb",
    TRAVEL: "#7c3aed", SOCIAL: "#be123c", OTHER: "#374151",
  };
  const typePalette = { TODO: "#0f766e", REMINDER: "#b45309", IMPORTANT_DATE: "#b91c1c", PAYMENT: "#166534" };
  const color = palette[category] || typePalette[type] || "#374151";
  return { backgroundColor: color, borderColor: color, textColor: "#ffffff" };
}
